import { Box } from "@mui/system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  EACH_BATCH_SIZE,
  EMITTER_ERROR,
  EMITTER_SUCCESS,
  IMPORT_COLUMN_PROPERTY_TYPES,
  IMPORT_CREATE_TYPE,
  IMPORT_EMPTY_FIELD_OPTIONS,
} from "../../../../utils/constants";
import * as Papaparse from "papaparse";
import {
  getErrorMessage,
  match_import_fields,
  FIELD_TYPE_LABELS,
} from "../../../../utils/helpers";
import { Uploader } from "../../../../utils/helpers/Uploader";
import { ErrorMessage } from "../../../../components";
import { Modal } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { getRequest, postRequest } from "../../../../Apis";
import {
  contactListEndPoints,
  crmEndpoints,
  importEndpoints,
} from "../../../../utils/constants/httpConstants";
import { useNavigate } from "react-router-dom";
import CustomModal from "../../../../components/Modals/CustomModal";
import { STRINGS } from "../../strings";
import ProgressEmitter from "../../../../components/Loaders/ProgressEmitter";
import classes from "./CrmComp.module.css";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import {
  ACTIONS,
  BASE,
  BORDER,
  ERROR,
  GREY,
  PURPLE,
  SUCCESS,
  WARNING,
} from "../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../utils/constants/UI";
import {
  borderRadius,
  SCROLL_BAR,
} from "../../../../utils/constants/UI/uiConstants";
import Checkbox from "../../../../components/Buttons/Checkbox/Checkbox";
import { AutoCompleteCustom } from "../../../../components/Autocomplete/AutoCompleteCustom";
import { Button } from "../../../../components/Buttons";
import InputField from "../../../../components/InputFields/InputField";
import StatusChip from "../../../../components/StatusChip/StatusChip";
import IconComponent from "../../../../components/Icon/IconComponent";
import {
  ATLEAS_ONE_FIELD,
  IMPORT_COMPANY_WEBSITE_EMAIL_MANDATORY,
  IMPORT_COMPANY_WEBSITE_MANDATORY,
  IMPORT_FILE_EMAIL,
  IMPORT_FILE_UPLOAD_SUCCESS,
} from "../../../../utils/constants/messages";

// One template for the header row and every body row, so the two can never drift
// apart. Fifth track is the per-row status chip.
const MAPPING_GRID_COLUMNS =
  "3rem minmax(0, 2.4fr) minmax(0, 1.5fr) minmax(0, 2.4fr) minmax(0, 1.4fr)";

// Each mapping state gets one tone and one label, derived once per row, so the
// chip, the row tint and the counts can never disagree with each other.
const ROW_STATUS = {
  skipped: {
    variant: "",
    label: "Not imported",
    background: BASE.BACKGROUND_LIGHT,
  },
  unmapped: {
    variant: "yellow",
    label: "Unmapped",
    background: WARNING.W_50,
  },
  auto: {
    variant: "green",
    label: "Auto-matched",
    background: BASE.WHITE,
  },
  manual: {
    variant: "blue",
    label: "Mapped",
    background: BASE.WHITE,
  },
  custom: {
    variant: "purple",
    label: "Custom field",
    background: PURPLE.W_50,
  },
  // Same target as `custom`, but reached by the matcher rather than by the rep.
  // Without its own state the two are indistinguishable in the grid, so nobody
  // can tell which of their own properties the modal picked for them.
  autoCustom: {
    variant: "purple",
    label: "Auto-matched",
    background: PURPLE.W_50,
  },
};

// The CRM stores one dynamic slot per tenant property and refuses the one past
// this count. It mirrors DYNAMIC_FIELD_LIMIT on the CRM service, which stays
// the authority: this only lets the modal warn before spending a round trip.
const DYNAMIC_FIELD_LIMIT = 50;

// object_type as the CRM spells it, keyed off the row's People/Company setting.
const CRM_OBJECT_TYPE_PEOPLE = "Contact";
const CRM_OBJECT_TYPE_COMPANY = "Company";

// What the CRM returns when a property key is already in use.
const CRM_PROPERTY_CONFLICT_STATUS = 409;

// Values the list service validates against (permission private|team, type
// file|folder). A list made here is always the rep's own single list.
// Where inline-created properties land when the workspace has no group yet.
const DEFAULT_GROUP_NAME = "Imported Fields";
const LIST_PERMISSION_PRIVATE = "private";
const LIST_TYPE_FILE = "file";
const LIST_PAGE_SIZE = 50;

// NOTE: this modal used to cap an import at 200 contacts per list (it counted
// the file's rows and the list's existing members, then refused anything that
// would go over). That cap is gone by product decision: a file of any size is
// sent through, and whatever the list service does with it is the list
// service's call.

// Ids for the two "create one instead" rows. sanitiseFieldName strips every
// character outside [A-Za-z0-9 ] and collapses the spaces, so a key the CRM
// mints can never carry a doubled or leading underscore and can never collide
// with these, whatever the rep types as a name.
const CREATE_FIELD_OPTION_ID = "__create_field__";
const CREATE_LIST_OPTION_ID = "__create_list__";
const NO_LIST_OPTION_ID = "__no_list__";

// The CRM derives a property's key from its display name the moment it is
// created: name.toLowerCase().replace(/\s+/g, "_"). It sanitises nothing else,
// so "Seed Funding ($M)" would be stored under seed_funding_($m), a key no
// filter can address. Clean the name here, and show the rep the key that name
// produces, so the FE never has to invent a key of its own and drift from the
// one the CRM computes.
const sanitiseFieldName = (name) =>
  (name || "")
    .replace(/[^A-Za-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toFieldKey = (name) =>
  sanitiseFieldName(name).toLowerCase().replace(/\s+/g, "_");

// Copy used only by this modal. STRINGS is shared across CRM screens, so new
// wording for one modal stays here rather than growing that file.
const MAPPING_COPY = {
  STATUS: "Status",
  DESCRIPTION:
    "Tick the columns you want to bring in and point each one at the field it belongs to.",
  SAMPLE_PREFIX: "e.g. ",
  NO_SAMPLE: "No value in the first row",
  EMPTY_TITLE: "Nothing to map",
  EMPTY_DETAIL:
    "No column names were found in the first row of this file. Close this, put the column names in row one, and upload again.",
  FILE_LOST_ERROR:
    "This file is no longer attached to the import — that happens if the page was reloaded or the file was already imported. Close this and upload it again.",
  MISSING_INDEX_ERROR: (labels) =>
    `${labels.join(", ")} ${
      labels.length === 1
        ? "has no column position"
        : "have no column position"
    } from the file, so those values would be read out of the wrong column. Close this and upload the file again.`,
  MAPPED_COUNT: (mapped, total) => `${mapped} of ${total} mapped`,
  IMPORT_CTA: (count) =>
    `Import (${count} ${count === 1 ? "field" : "fields"})`,
  CREATE_FIELD_OPTION: "Create a new field from this column",
  CREATE_FIELD_TITLE: (column) => `New field for "${column}"`,
  CREATE_FIELD_CTA: "Create field",
  FIELD_NAME_LABEL: "Field name",
  FIELD_NAME_PLACEHOLDER: "Name the rep will see",
  FIELD_TYPE_LABEL: "Field type",
  FIELD_GROUP_LABEL: "Group",
  FIELD_KEY_PREVIEW: (key) =>
    key ? `Stored as ${key}` : "Type a name to see how it will be stored",
  FIELD_CAPACITY: (used, limit) =>
    `Contact and company properties in use: ${used} of ${limit}. Deal properties share the same limit.`,
  FIELD_NAME_REQUIRED: "Give the field a name using letters or numbers.",
  FIELD_NAME_BUILTIN: (label) =>
    `${label} is already a standard ReachIQ field. Map the column to ${label} in the list above instead of creating a second one, otherwise the value would be written to a field nothing reads.`,
  FIELD_NAME_EXISTS: (label) =>
    `You already have a property called "${label}". Map the column to it instead.`,
  FIELD_LIMIT_REACHED: (limit) =>
    `Your workspace is using all ${limit} custom properties the CRM allows. Delete one under Manage Properties, then create this field.`,
  FIELD_NO_GROUP: (objectType) =>
    `A new property has to sit in a ${objectType.toLowerCase()} property group and this workspace has none yet. Create one under Manage Properties, then come back.`,
  FIELD_CREATED_UNREADABLE:
    "The field was created but this screen could not read it back, so the column was left unmapped. Reload the import page and map it again.",
  FINISH_FIELD_FIRST: "Create the new field or cancel it to carry on.",
  BULK_BAR_TEXT: (count) =>
    `${count} selected ${
      count === 1 ? "column has" : "columns have"
    } no field yet.`,
  BULK_CTA: (count) =>
    `Create ${count} ${count === 1 ? "field" : "fields"} from unmapped columns`,
  BULK_TITLE: "Create fields from unmapped columns",
  BULK_SUMMARY: (count, remaining) =>
    `${count} new ${
      count === 1 ? "property" : "properties"
    } will be created. ${remaining} of the ${DYNAMIC_FIELD_LIMIT} the CRM allows are still free.`,
  BULK_TYPE_NOTE:
    "Every one is created as Text Input, in the first property group for its object type. Change a type later under Manage Properties.",
  BULK_KEYS_NOTE:
    "The CRM builds the key from the name, so read these before you commit:",
  BULK_STOP_NOTE:
    "One request per property, one after another. If one fails the run stops there and everything created before it is kept and mapped.",
  BULK_BUILTIN_EXCLUDED: (count) =>
    `${count} ${
      count === 1 ? "column matches a" : "columns match a"
    } standard ReachIQ field and ${
      count === 1 ? "is" : "are"
    } left out. Map ${
      count === 1 ? "it" : "them"
    } to the standard field in the row above, otherwise the value goes to a field nothing reads:`,
  BULK_REUSE: (count) =>
    `${count} ${
      count === 1 ? "column already has" : "columns already have"
    } a property with the same key. ${
      count === 1 ? "It" : "They"
    } will be mapped to it instead of creating a second one:`,
  BULK_UNNAMED: (count) =>
    `${count} ${
      count === 1 ? "column has" : "columns have"
    } no letters or numbers in the heading to build a key from, so ${
      count === 1 ? "it is" : "they are"
    } left out. Map ${count === 1 ? "it" : "them"} by hand:`,
  BULK_OVER_CAPACITY: (count, remaining, limit) =>
    remaining === 0
      ? `Your workspace is using all ${limit} custom properties the CRM allows, so none of these ${count} can be created. Delete some under Manage Properties, or map these columns to fields you already have.`
      : `These columns need ${count} new properties and only ${remaining} of the ${limit} the CRM allows are left. Nothing was created. Map ${
          count - remaining
        } of them to fields you already have, or free up space under Manage Properties.`,
  BULK_PROGRESS: (current, total, name) =>
    `Creating ${current} of ${total}: ${name}`,
  BULK_CREATED_TOAST: (count) =>
    `${count} ${count === 1 ? "field" : "fields"} created and mapped.`,
  BULK_FAILED_TITLE: (created, total) =>
    `Stopped after ${created} of ${total}. The ${created} below exist in the CRM and their columns are mapped.`,
  BULK_FAILED_DETAIL: (name) => `"${name}" could not be created:`,
  BULK_NOT_ATTEMPTED: (count) =>
    `${count} ${
      count === 1 ? "column was" : "columns were"
    } not attempted. Retry picks up from there and never asks for a property it already created.`,
  BULK_CREATED_LIST: "Created and mapped:",
  BULK_CONFIRM_CTA: (count) =>
    `Create ${count} ${count === 1 ? "field" : "fields"}`,
  BULK_RETRY_CTA: (count) => `Retry the remaining ${count}`,
  BULK_CLOSE_CTA: "Close",
  BULK_FINISH_FIRST: "Finish creating the fields, or cancel, to carry on.",
  LIST_LABEL: "Also add these contacts to a list",
  LIST_HINT:
    "Contacts still land in Imported. A list is one more place they show up, and it costs no credits.",
  LIST_PLACEHOLDER: "None selected",
  NO_LIST_OPTION: "Do not add to a list",
  CREATE_LIST_OPTION: "Create a new list",
  LIST_NAME_PLACEHOLDER: "List name",
  LIST_NAME_MIN: "List names need at least 3 characters.",
  LIST_NAME_MAX: "List names can be at most 50 characters.",
  LIST_CREATED_UNREADABLE:
    "The list was created but did not come back in the list of lists. Pick it from the dropdown once it appears.",
};

const styles = {
  flex: {
    display: "flex",
    paddingTop: "1rem",
    justifyContent: "space-between",
  },
  dropdown: {
    padding: "0.5rem 0",
  },
  containDropdowns: {
    display: "grid",
    // Three across: import type, empty values and the destination list. They are
    // all one-line selects, so stacking the list on its own row only cost height
    // in a modal that is already tall.
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "1rem",
    paddingTop: "1rem",
  },
  // CustomModal's shell caps its own height but sets no overflow, so a wide CSV
  // used to push the footer past the viewport. Scroll the grid, not the modal.
  gridScrollContainer: {
    ...SCROLL_BAR,
    maxHeight: "40vh",
    overflowY: "auto",
    border: `1px solid ${BORDER.DEFAULT}`,
    borderRadius: borderRadius.primary,
  },
  gridHeaderRow: {
    display: "grid",
    gridTemplateColumns: MAPPING_GRID_COLUMNS,
    backgroundColor: ACTIONS.HOVER,
    borderBottom: `1px solid ${BORDER.DEFAULT}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  gridHeaderCell: {
    padding: "0.75rem",
  },
  gridRow: {
    display: "grid",
    gridTemplateColumns: MAPPING_GRID_COLUMNS,
    alignItems: "center",
    borderBottom: `1px solid ${BORDER.LIGHT}`,
  },
  gridCell: {
    padding: "0.5rem 0.75rem",
    minWidth: 0,
  },
  checkboxCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.5rem",
  },
  columnNameCell: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "0.5rem 0.75rem",
    minWidth: 0,
  },
  statusCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0.5rem 0.75rem",
    minWidth: 0,
  },
  statusStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "2px",
    minWidth: 0,
    maxWidth: "100%",
  },
  // CustomModal caps its own shell but its body sets no height of its own, so
  // the controls under the grid can push the footer past the bottom edge on a
  // short viewport. Cap the body and let it scroll.
  modalBodyScroll: {
    maxHeight: "calc(100vh - 16rem)",
  },
  createFieldPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    marginTop: "0.75rem",
    padding: "0.75rem",
    border: `1px solid ${PURPLE.W_100}`,
    borderRadius: borderRadius.primary,
    backgroundColor: PURPLE.W_50,
  },
  createFieldHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "1rem",
    minWidth: 0,
  },
  createFieldGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr)",
    gap: "0.75rem",
  },
  createFieldControl: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  createFieldActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  },
  // Grid, not flex, for the same reason listCreateRow is: Button carries
  // width:100%, so as a flex sibling it fights the text for the row.
  bulkBar: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: "1rem",
    marginTop: "0.75rem",
    padding: "0.5rem 0.75rem",
    border: `1px solid ${PURPLE.W_100}`,
    borderRadius: borderRadius.primary,
    backgroundColor: PURPLE.W_50,
  },
  bulkBarText: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
  },
  bulkSection: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    minWidth: 0,
  },
  // 23 rows is the case this exists for, so the list scrolls rather than
  // pushing the panel's own actions out of the modal.
  bulkList: {
    ...SCROLL_BAR,
    maxHeight: "13vh",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "0.5rem",
    border: `1px solid ${BORDER.LIGHT}`,
    borderRadius: borderRadius.primary,
    backgroundColor: BASE.WHITE,
  },
  bulkListRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    alignItems: "baseline",
    gap: "0.75rem",
  },
  listSection: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    paddingTop: "0.5rem",
  },
  listControls: {
    // Only the inline create-list row lives here now; the picker itself moved up
    // into the three-across dropdown grid.
    display: "block",
    paddingTop: "0.5rem",
  },
  // Grid rather than flex: Button carries width:100%, so as a flex sibling it
  // fights the input for the row instead of sizing to its label.
  listCreateRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto auto",
    alignItems: "start",
    gap: "8px",
  },
  listCreateInput: {
    minWidth: 0,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "2.5rem 1rem",
    textAlign: "center",
    border: `1px dashed ${BORDER.DEFAULT}`,
    borderRadius: borderRadius.primary,
    backgroundColor: BASE.BACKGROUND_LIGHT,
  },
  errorBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginTop: "0.75rem",
    padding: "0.5rem 0.75rem",
    border: `1px solid ${ERROR.W_100}`,
    borderRadius: borderRadius.primary,
    backgroundColor: ERROR.W_50,
  },
  footerHint: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    maxWidth: "34vw",
  },
};

const MappingFieldModal = ({
  open,
  setOpen,
  file,
  columns,
  setFile,
  peopleDynamicFields,
  companyDynamicFields,
  predefinedPeopleCustomFields,
  predefinedCompanyCustomFields,
  getImportedFilesList,
  onCustomFieldCreated,
  preselectedListId,
  preselectedListName,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userDetailsSelector = useSelector(
    (state) => state?.userDetails?.payload,
  );

  const [allColumns, setAllColumns] = useState(columns || []);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [fillEmptyId, setFillEmptyId] = useState([
    IMPORT_EMPTY_FIELD_OPTIONS[0].id,
  ]);
  const [fillEmptyValue, setFillEmptyValue] = useState([
    IMPORT_EMPTY_FIELD_OPTIONS[0].label,
  ]);
  const [uploadTypeValue, setUploadTypeValue] = useState([
    IMPORT_CREATE_TYPE[2].label,
  ]);
  const [uploadTypeId, setUploadTypeId] = useState([IMPORT_CREATE_TYPE[2].id]);
  const [selectMatchColumnsOnly, setSelectMatchColumnsOnly] = useState(false);
  const [addToCrm, setAddToCrm] = useState(false);
  const [addToImported, setAddToImported] = useState(false);
  const [peopleCustomFields, setPeopleCustomFields] = useState(
    peopleDynamicFields || [],
  );
  const [companyCustomFields, setCompanyCustomFields] = useState(
    companyDynamicFields || [],
  );
  const [predefinedPeopleFields, setPredefinedPeopleFields] = useState(
    predefinedPeopleCustomFields || [],
  );
  const [predefinedCompanyFields, setPredefinedCompanyFields] = useState(
    predefinedCompanyCustomFields || [],
  );
  const [errorMessage, setErrorMessage] = useState({ id: "", message: "" });
  const [columnsUploading, setColumnsUploading] = useState(false);
  const [importCreateType, setImportCreateType] = useState([
    ...IMPORT_CREATE_TYPE,
  ]);
  // Per-row search text for the ReachIQ-property dropdown, keyed by row index.
  const [propertySearch, setPropertySearch] = useState({});
  // Distinguishes "this file genuinely has no columns" from "the parse has not
  // finished yet", so the empty state cannot flash on a fast upload.
  const [columnsParsed, setColumnsParsed] = useState(false);

  // Inline field creation. createFieldRow holds the row the form was opened
  // from; null means the form is closed.
  const [createFieldRow, setCreateFieldRow] = useState(null);
  const [createFieldName, setCreateFieldName] = useState("");
  // Type and group are no longer asked for: everything is created as Text Input in
  // the object type's first group, which is made on the fly if the workspace has
  // none. The setters stay because the reset and resolve paths still drive them;
  // only the values are unread now that the two selects are gone.
  const [, setCreateFieldTypeId] = useState([FIELD_TYPE_LABELS[0].id]);
  const [createFieldTypeValue, setCreateFieldTypeValue] = useState([
    FIELD_TYPE_LABELS[0].label,
  ]);
  const [, setPropertyGroups] = useState([]);
  const [, setGroupsLoading] = useState(false);
  const [createFieldGroupId, setCreateFieldGroupId] = useState([]);
  const [, setCreateFieldGroupValue] = useState([]);
  const [createFieldError, setCreateFieldError] = useState("");
  const [creatingField, setCreatingField] = useState(false);
  // Properties minted from this modal. They are not in the props the modal was
  // seeded with, so every lookup that resolves an id back to an option has to
  // read them from here as well.
  const [createdCustomFields, setCreatedCustomFields] = useState({
    people: [],
    company: [],
  });
  // Every key the CRM has already accepted from this modal, whether or not the
  // read-back that follows it succeeded. A run that stops halfway leaves real
  // properties on the server, so a retry has to skip their create call: without
  // this the CRM would answer "property already exists" on the retry, or worse,
  // the read-back-failed ones would be posted a second time.
  const createdFieldKeysRef = useRef({ people: [], company: [] });

  // Bulk field creation, driven off the same single-create machinery below.
  // bulkPanelOpen shows the preview; bulkRun holds one run's progress and its
  // outcome, and survives the run so a partial failure can be reported.
  const [bulkPanelOpen, setBulkPanelOpen] = useState(false);
  const [bulkGroups, setBulkGroups] = useState({ people: "", company: "" });
  const [bulkGroupsLoading, setBulkGroupsLoading] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkRun, setBulkRun] = useState(null);

  // Optional destination list.
  const [contactLists, setContactLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listSearch, setListSearch] = useState("");
  // Seeded when the rep came here from a specific marketing list. The name comes
  // along so the control reads correctly before the list fetch resolves.
  const [selectedListId, setSelectedListId] = useState(
    preselectedListId ? [preselectedListId] : [],
  );
  const [selectedListValue, setSelectedListValue] = useState(
    preselectedListName ? [preselectedListName] : [],
  );
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [listError, setListError] = useState("");
  const [creatingList, setCreatingList] = useState(false);

  // AutoCompleteCustom memoises its debounce on the identity of handleSearch, so
  // a fresh arrow per render would reset the timer on every keystroke and defeat
  // the debounce. Cache one stable handler per row instead.
  const searchHandlersRef = useRef({});
  const getPropertySearchHandler = (index) => {
    if (!searchHandlersRef.current[index]) {
      searchHandlersRef.current[index] = (event) => {
        const value = event?.target?.value || "";
        setPropertySearch((prev) => ({ ...prev, [index]: value }));
      };
    }
    return searchHandlersRef.current[index];
  };

  // Stable for the same reason getPropertySearchHandler is cached above:
  // AutoCompleteCustom memoises its debounce on the identity of handleSearch,
  // and this one reaches the network, so a fresh arrow per render would mean a
  // request per keystroke.
  const handleListSearch = useCallback(
    (event) => setListSearch(event?.target?.value || ""),
    [],
  );

  const filterByPropertySearch = (items, index) => {
    const search = propertySearch?.[index]?.trim()?.toLowerCase();
    if (!search) {
      return items;
    }
    return items?.filter((item) =>
      item?.label?.toString()?.toLowerCase()?.includes(search),
    );
  };

  // Every tenant-created property the modal knows about: the ones it was seeded
  // with plus the ones the rep created here. A field created in this session is
  // in neither prop array, so a lookup that reads only the props would resolve
  // it to nothing and hand it to the built-in path, writing the value under a
  // slug the CRM read path never looks at.
  const getTenantCustomFields = (isPeople) => [
    ...((isPeople ? predefinedPeopleCustomFields : predefinedCompanyCustomFields) ||
      []),
    ...(createdCustomFields?.[isPeople ? "people" : "company"] || []),
  ];

  // A custom field is one the tenant created themselves; those are the rows that
  // must travel as a pre-allocated slot key (people_N / company_N) rather than a
  // built-in field name. The component's local *CustomFields state removes an
  // option once it is taken, so the lookup has to go against the untouched props.
  const findCustomFieldOption = (propertyType, propertyValue) => {
    if (!propertyValue) {
      return null;
    }
    const is_people = propertyType === STRINGS.PEOPLE_SMALL;
    // A built-in always wins. Both id spaces are the same slug form, and the CRM
    // only checks a new property's slug against other custom properties — never
    // against the built-in list. So a tenant property called "Job Title" yields
    // id "job_title" and collides with the built-in one. Without this guard a rep
    // mapping to the BUILT-IN field would have the value written to the custom
    // slot instead, leaving the real column empty, with a 200 and no error.
    const builtins = (is_people ? peopleDynamicFields : companyDynamicFields) || [];
    if (builtins?.some((item) => item?.id === propertyValue)) {
      return null;
    }
    const source = getTenantCustomFields(is_people);
    return source?.find((item) => item?.id === propertyValue) || null;
  };

  const getRowStatusKey = (column) => {
    if (!column?.selected) {
      return "skipped";
    }
    if (!column?.property_value) {
      return "unmapped";
    }
    const is_auto = column?.auto_matched_value === column?.property_value;
    if (findCustomFieldOption(column?.property_type, column?.property_value)) {
      return is_auto ? "autoCustom" : "custom";
    }
    return is_auto ? "auto" : "manual";
  };

  // send only property_value in items.
  const checkSelectedFields = (items) => {
    const all_company_fields = [
      ...companyDynamicFields,
      ...getTenantCustomFields(false),
    ].map((item) => item.id);
    const all_people_fields = [
      ...peopleDynamicFields,
      ...getTenantCustomFields(true),
    ].map((item) => item.id);
    const is_people_field_available = all_people_fields.some((item) =>
      items.includes(item),
    );
    const is_company_field_available = all_company_fields.some((item) =>
      items.includes(item),
    );
    return { is_people_field_available, is_company_field_available };
  };

  async function readFileChunk(time, index, fil) {
    let reader = new FileReader();
    let blob = fil.slice(
      EACH_BATCH_SIZE * index,
      EACH_BATCH_SIZE * (index + 1),
    );
    reader.onload = async function (e) {
      const each_parsed_string = e.target.result;
      const ParsedRes = Papaparse.parse(each_parsed_string);
      const result = [];
      const tempData = ParsedRes.data[0];
      // First data row, read only to show a sample value per column. Nothing here
      // may reorder or re-index tempData - column_index is a raw CSV position and
      // the worker parses the file headerless.
      const firstDataRow = ParsedRes.data[1];
      const property_values = [];
      for (let i = 0; i < tempData?.length; i++) {
        if (tempData?.[i]) {
          let eachObj = { column: tempData?.[i], selected: true };
          // i is the position in the RAW header row, not in `result`. Blank
          // headers are skipped below so they never show up as a mappable row,
          // which makes the position inside `result` smaller than the real one
          // from the first blank header onwards. The worker reads records by the
          // raw number, so that raw number is what has to survive to submit.
          eachObj.csv_index = i;
          eachObj.sample_value = firstDataRow?.[i]?.toString()?.trim() || "";
          const each_property_value = match_import_fields(
            tempData?.[i],
            peopleCustomFields,
            companyCustomFields,
            predefinedPeopleCustomFields,
            predefinedCompanyCustomFields,
          );

          eachObj.property_value = each_property_value.property_value;
          eachObj.property_type = each_property_value.property_type;
          eachObj.property_type_label = each_property_value.property_type_label;
          // Remembered so a later manual pick can be told apart from what the
          // matcher guessed, which is what the per-row status chip reports.
          eachObj.auto_matched_value = each_property_value.property_value;
          let each_property_label = "";
          const custom_fields =
            each_property_value.property_type === STRINGS.PEOPLE_SMALL
              ? [...peopleCustomFields, ...(predefinedPeopleCustomFields || [])]
              : [
                  ...companyCustomFields,
                  ...(predefinedCompanyCustomFields || []),
                ];
          const each_property_find = custom_fields?.filter(
            (item) => item?.id === each_property_value?.property_value,
          );
          if (each_property_find?.length) {
            each_property_label = each_property_find?.[0]?.label;
          }
          eachObj.property_label = each_property_label;

          each_property_value?.property_value &&
            property_values.push(each_property_value?.property_value);
          result.push(eachObj);
        }
      }

      // comapny and people changes here.
      const newPeopleDynamicFields = peopleCustomFields?.filter((item) => {
        return !property_values?.includes(item?.id);
      });
      const newCompanyDynamicFields = companyCustomFields?.filter((item) => {
        return !property_values?.includes(item?.id);
      });

      const newPredefinedPeopleFields = predefinedPeopleFields?.filter(
        (item) => {
          return !property_values?.includes(item?.id);
        },
      );
      const newPredefinedCompanyFields = predefinedCompanyFields?.filter(
        (item) => {
          return !property_values?.includes(item?.id);
        },
      );

      // adding isCustomField Key here.
      const newPeopleDynamicFields1 = newPeopleDynamicFields?.map((item) => {
        return { ...item, isCustomField: false };
      });
      const newPredefinedPeopleFields1 = newPredefinedPeopleFields?.map(
        (item) => {
          return { ...item, isCustomField: true };
        },
      );

      const newCompanyDynamicFields1 = newCompanyDynamicFields?.map((item) => {
        return { ...item, isCustomField: false };
      });
      const newPredefinedCompanyFields1 = newPredefinedCompanyFields?.map(
        (item) => {
          return { ...item, isCustomField: true };
        },
      );

      setPeopleCustomFields([
        ...newPeopleDynamicFields1,
        ...newPredefinedPeopleFields1,
      ]);
      setCompanyCustomFields([
        ...newCompanyDynamicFields1,
        ...newPredefinedCompanyFields1,
      ]);
      setPredefinedPeopleFields(newPredefinedPeopleFields);
      setPredefinedCompanyFields(newPredefinedCompanyFields);

      const selected_proprty_values = result
        .filter((item) => item?.selected)
        .map((item) => item.property_value);
      const field_selection = checkSelectedFields(selected_proprty_values);
      if (
        field_selection.is_company_field_available &&
        field_selection.is_people_field_available
      ) {
        // update import Type here.
        setImportCreateType(
          importCreateType.filter((item) => item.id !== "update_only"),
        );
      }
      setAllColumns(result);
      setColumnsParsed(true);
      // make file sending endpoint here.
    };
    reader.readAsText(blob);
    return new Promise((resolve) => setTimeout(resolve, time));
  }

  const initiateUploadingProcess = () => {
    let percentage = undefined;
    const videoUploaderOptions = {
      fileName: file?.name || "foo",
      user_email: userDetailsSelector?.email,
      file: file,
      initialEndpoint: importEndpoints.uploadFileInitialEndpoint,
      multipartEndpoint: importEndpoints.uploadFileMultipartEndpoint,
      finalEndpoint: importEndpoints.uploadFilefinalEndpoint,
      navigate: navigate,
    };
    const uploader = new Uploader(videoUploaderOptions);

    uploader
      .onProgress(({ percentage: newPercentage }) => {
        if (newPercentage !== percentage) {
          percentage = newPercentage;
          percentage >= uploadPercentage && setUploadPercentage(percentage);
        }
      })
      .onError((error) => {
        setUploadFailed(true);
        EventEmitter.emit(
          EMITTER_ERROR,
          getErrorMessage(error?.data?.data?.errors || {}, error?.data?.message),
        );
        setFile("");
        setOpen(false);
      });

    uploader.start();
  };

  useEffect(() => {
    if (!file) {
      return;
    }
    setUploadFailed(false);
    setColumnsParsed(false);
    initiateUploadingProcess();
    (async () => {
      await readFileChunk(1000, 0, file);
    })();
  }, [file]);

  // The whole-file row count that used to live here is gone with the per-list
  // cap: nothing needs to know how many rows the file holds before the worker
  // reads it, so a large CSV is no longer streamed twice.

  useEffect(() => {
    if (uploadPercentage === 100) {
      EventEmitter.emit(EMITTER_SUCCESS, IMPORT_FILE_UPLOAD_SUCCESS);
    }
  }, [uploadPercentage, dispatch]);

  // Lists are only needed once the mapping step is on screen, and again for
  // every search the rep types into the picker.
  useEffect(() => {
    if (uploadPercentage !== 100) {
      return;
    }
    fetchContactLists(listSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadPercentage, listSearch]);

  // Any edit to the mapping invalidates whatever the last failed submit
  // complained about, so the banner must not outlive the state it described.
  const clearErrorMessage = () => setErrorMessage({ id: "", message: "" });

  const handlePropertyTypeChange = (data, index, column) => {
    clearErrorMessage();
    const currentValue = data?.id[0];
    const currentPropertyValue = data?.label[0];
    const itemsClone = [...allColumns];
    const prevArray = itemsClone.slice(0, index);
    const nextArray = itemsClone.slice(index + 1);
    if (currentValue === "company") {
      let allFields = [...peopleDynamicFields];
      let changed_field = allFields?.filter((item) => {
        return item?.label === column?.property_label;
      });
      setPeopleCustomFields([...peopleCustomFields, ...changed_field]);
    } else {
      let allFields = [...companyDynamicFields];
      let changed_field = allFields?.filter((item) => {
        return item?.label === column?.property_label;
      });
      setCompanyCustomFields([...companyCustomFields, ...changed_field]);
    }
    const currentElement = itemsClone[index];
    currentElement.property_type = currentValue;
    currentElement.property_type_label = currentPropertyValue;
    currentElement.property_value = "";
    currentElement.property_label = "";
    // Switching People <-> Company throws the matcher's guess away, so whatever
    // gets picked next is a manual choice.
    currentElement.auto_matched_value = "";
    setAllColumns([...prevArray, currentElement, ...nextArray]);
  };

  const handlePropertyValueChange = (e, index, previousValue, type) => {
    clearErrorMessage();
    const currentValue = e?.id[0];
    // The last row of the dropdown is an action, not a target. It opens the
    // inline form and leaves the row on whatever it was mapped to until the
    // property actually exists in the CRM.
    if (currentValue === CREATE_FIELD_OPTION_ID) {
      openCreateFieldForm(index, allColumns?.[index]);
      return;
    }
    const itemsClone = [...allColumns];
    const prevArray = itemsClone.slice(0, index);
    const nextArray = itemsClone.slice(index + 1);
    const currentElement = itemsClone[index];
    const current_custom_fields =
      type === STRINGS.PEOPLE_SMALL ? peopleCustomFields : companyCustomFields;
    currentElement.property_value = currentValue;
    currentElement.property_label = current_custom_fields?.filter(
      (item) => item?.id === currentValue,
    )?.[0]?.label;

    setAllColumns([...prevArray, currentElement, ...nextArray]);

    if (type === STRINGS.PEOPLE_SMALL) {
      const newCustomFields = [
        ...peopleCustomFields?.filter((item) => item?.id !== currentValue),
      ];
      if (previousValue) {
        let prevValue;
        prevValue = peopleDynamicFields?.filter(
          (item) => item?.id === previousValue,
        )?.[0];
        if (prevValue) {
          prevValue.isCustomField = false;
        } else {
          prevValue = getTenantCustomFields(true)?.filter(
            (item) => item?.id === previousValue,
          )?.[0];
          // A field created in this modal is not in the props array, so before
          // getTenantCustomFields this lookup returned undefined and the next
          // line threw. Keep the assignment guarded either way.
          if (prevValue) {
            prevValue.isCustomField = true;
          }
        }
        prevValue && newCustomFields.push(prevValue);
      }
      setPeopleCustomFields(newCustomFields);
    } else {
      const newCustomFields = [
        ...companyCustomFields?.filter((item) => item?.id !== currentValue),
      ];
      if (previousValue) {
        let prevValue;
        prevValue = companyDynamicFields?.filter(
          (item) => item?.id === previousValue,
        )?.[0];
        if (prevValue) {
          prevValue.isCustomField = false;
        } else {
          prevValue = getTenantCustomFields(false)?.filter(
            (item) => item?.id === previousValue,
          )?.[0];
          if (prevValue) {
            prevValue.isCustomField = true;
          }
        }
        prevValue && newCustomFields.push(prevValue);
      }
      setCompanyCustomFields(newCustomFields);
    }

    settingImportTypeValues(allColumns);
  };

  // grp_id is not optional on create-dynamic: the CRM reads mappings.grp_id[0]
  // and, when it is missing, drops into its group-creation branch and answers
  // "group name is required" for what looked like a property call. The single
  // form and the bulk run both need the same default group, so the fetch is
  // kept apart from the form state it usually fills in.
  // get-group, not get-group-by-type: the latter answers an empty list even when
  // groups exist, which is what made the panel claim the workspace had none. This
  // is the same GET the properties screen uses, and its groups live under
  // data.hits with grp_type like ["Contact"] or ["Contact/Company"].
  const fetchPropertyGroups = async (isPeople) => {
    const object_type = isPeople
      ? CRM_OBJECT_TYPE_PEOPLE
      : CRM_OBJECT_TYPE_COMPANY;
    const response = await getRequest(navigate, crmEndpoints.getGroupList);
    return (response?.data?.data?.hits || [])
      ?.filter(
        (item) =>
          item?._id &&
          item?.grp_name &&
          (item?.grp_type || []).some((type) =>
            String(type).includes(object_type),
          ),
      )
      ?.map((item) => ({ id: item?._id, label: item?.grp_name }));
  };

  // Default to the first group the CRM returns for this object type, which is
  // the order the properties screen lists them in, and leave the rep able to
  // move it.
  const loadPropertyGroups = async (isPeople) => {
    const object_type = isPeople
      ? CRM_OBJECT_TYPE_PEOPLE
      : CRM_OBJECT_TYPE_COMPANY;
    try {
      setGroupsLoading(true);
      const all_groups = await fetchPropertyGroups(isPeople);
      // Fields created from an import go in their own group rather than joining
      // ReachIQ's built-in one, so a workspace's own properties stay as they were.
      let groups = (all_groups || []).filter(
        (group) =>
          String(group?.label || "").toLowerCase() ===
          DEFAULT_GROUP_NAME.toLowerCase(),
      );
      if (!groups?.length) {
        // A property must belong to a group, and a fresh workspace has none. Making
        // the rep leave for Manage Properties to create an empty container first is
        // a dead end for what should be one click, so create it for them. The
        // create call answers with the new id; the listing endpoint does not show
        // it straight away, so use what we were handed rather than re-reading.
        const response = await postRequest(navigate, crmEndpoints.createDynamic, {
          query: { grp_name: DEFAULT_GROUP_NAME, grp_type: [object_type] },
        });
        const created_id = response?.data?.data?.insertedId;
        if (!created_id) {
          setCreateFieldError(MAPPING_COPY.FIELD_NO_GROUP(object_type));
          setPropertyGroups([]);
          return;
        }
        groups = [{ id: created_id, label: DEFAULT_GROUP_NAME }];
      }
      setPropertyGroups(groups);
      setCreateFieldGroupId([groups[0].id]);
      setCreateFieldGroupValue([groups[0].label]);
    } catch (error) {
      setPropertyGroups([]);
      setCreateFieldError(
        getErrorMessage(error?.data?.data?.errors || {}, error?.data?.message),
      );
    } finally {
      setGroupsLoading(false);
    }
  };

  const openCreateFieldForm = (index, column) => {
    // A bulk run is already posting properties one after another and mapping
    // rows as it goes. A second form open over it would let the rep create the
    // same column twice.
    if (bulkRun?.running) {
      return;
    }
    const isPeople = column?.property_type === STRINGS.PEOPLE_SMALL;
    setBulkPanelOpen(false);
    setCreateFieldRow({ index, column: column?.column, isPeople });
    // The header is only the starting point. The rep confirms or rewrites it,
    // because the CSV header is what their spreadsheet called the column, not
    // necessarily what the CRM should call the field.
    setCreateFieldName(sanitiseFieldName(column?.column));
    setCreateFieldTypeId([FIELD_TYPE_LABELS[0].id]);
    setCreateFieldTypeValue([FIELD_TYPE_LABELS[0].label]);
    setCreateFieldError("");
    loadPropertyGroups(isPeople);
  };

  const closeCreateFieldForm = () => {
    setCreateFieldRow(null);
    setCreateFieldName("");
    setCreateFieldError("");
  };

  // create-dynamic answers with the raw Mongo insert result, so the slot key
  // the importer needs (people_N / company_N) is not in its response. Re-read
  // the mapping list, the same source this modal was seeded from, and take the
  // entry the CRM just minted.
  const readBackCreatedField = async (isPeople, fieldKey) => {
    const response = await getRequest(
      navigate,
      importEndpoints.getCrmFieldlMapping,
    );
    const records = response?.data?.data || {};
    const source =
      (isPeople ? records?.people_custom_field : records?.company_custom_field) ||
      [];
    return source?.find((item) => item?.id === fieldKey) || null;
  };

  // Everything the CRM would refuse about a name, worked out before a round trip
  // is spent on it. One reading of the rules for the single form and the bulk
  // run, so the two can never disagree about what is creatable.
  const inspectFieldName = (name, isPeople) => {
    const clean_name = sanitiseFieldName(name);
    const field_key = toFieldKey(clean_name);
    // The CRM compares a new property's slug against other CUSTOM properties
    // only, never against the built-in ids, so "Job Title" would be accepted
    // and then lose every lookup to the built-in job_title.
    const builtins = (isPeople ? peopleDynamicFields : companyDynamicFields) || [];
    return {
      clean_name,
      field_key,
      builtin_conflict: field_key
        ? builtins?.find((item) => item?.id === field_key) || null
        : null,
      existing_custom: field_key
        ? getTenantCustomFields(isPeople)?.find(
            (item) => item?.id === field_key,
          ) || null
        : null,
    };
  };

  // ONE property per call, deliberately. create-dynamic files whatever it is
  // given under a single reference_field: an array of mappings becomes one
  // property document holding N entries that all share one slot, so the columns
  // would overwrite each other. N properties means N requests.
  // The CRM answers a duplicate property name with 409 and a fixed message, and
  // both are checked because the envelope carries the code in different places
  // depending on whether the request failed at the gateway or in the service.
  const isPropertyNameTakenError = (error) =>
    error?.status === CRM_PROPERTY_CONFLICT_STATUS ||
    error?.data?.serverCode === CRM_PROPERTY_CONFLICT_STATUS ||
    /already\s+taken/i.test(error?.data?.message || "");

  const createDynamicProperty = (isPeople, cleanName, grpId, typeValue) =>
    postRequest(navigate, crmEndpoints.createDynamic, {
      query: {
        // Same shape the properties screen posts. The key is deliberately
        // absent: the CRM derives it from the name, and a key sent from here
        // would drift from the one it computes.
        mappings: {
          object_type: [
            isPeople ? CRM_OBJECT_TYPE_PEOPLE : CRM_OBJECT_TYPE_COMPANY,
          ],
          grp_id: grpId,
          name: cleanName,
          type: typeValue,
          value: "",
          in_form: false,
        },
      },
    });

  // A property minted here is in neither prop array, so every lookup that
  // resolves an id has to be told about it, and the parent screen with it.
  const registerCreatedField = (created, isPeople) => {
    setCreatedCustomFields((previous) => ({
      ...previous,
      [isPeople ? "people" : "company"]: [
        ...(previous?.[isPeople ? "people" : "company"] || []),
        created,
      ],
    }));
    if (onCustomFieldCreated) {
      onCustomFieldCreated(created, isPeople);
    }
  };

  const rememberCreatedKey = (isPeople, fieldKey) => {
    const bucket = isPeople ? "people" : "company";
    if (!createdFieldKeysRef.current[bucket].includes(fieldKey)) {
      createdFieldKeysRef.current[bucket].push(fieldKey);
    }
  };

  const withFieldMapped = (columns, indexes, field) =>
    columns?.map((item, position) =>
      indexes?.includes(position)
        ? {
            ...item,
            property_value: field?.id,
            property_label: field?.label,
          }
        : item,
    );

  const handleCreateField = async () => {
    const isPeople = createFieldRow?.isPeople;
    const object_type = isPeople
      ? CRM_OBJECT_TYPE_PEOPLE
      : CRM_OBJECT_TYPE_COMPANY;
    const { clean_name, field_key, builtin_conflict, existing_custom } =
      inspectFieldName(createFieldName, isPeople);

    if (!field_key) {
      setCreateFieldError(MAPPING_COPY.FIELD_NAME_REQUIRED);
      return;
    }
    if (builtin_conflict) {
      setCreateFieldError(
        MAPPING_COPY.FIELD_NAME_BUILTIN(builtin_conflict?.label || field_key),
      );
      return;
    }
    if (existing_custom) {
      setCreateFieldError(
        MAPPING_COPY.FIELD_NAME_EXISTS(existing_custom?.label || field_key),
      );
      return;
    }
    if (customFieldsUsed >= DYNAMIC_FIELD_LIMIT) {
      setCreateFieldError(MAPPING_COPY.FIELD_LIMIT_REACHED(DYNAMIC_FIELD_LIMIT));
      return;
    }
    if (!createFieldGroupId?.[0]) {
      setCreateFieldError(MAPPING_COPY.FIELD_NO_GROUP(object_type));
      return;
    }

    try {
      setCreatingField(true);
      setCreateFieldError("");
      const response = await createDynamicProperty(
        isPeople,
        clean_name,
        createFieldGroupId,
        createFieldTypeValue,
      );
      rememberCreatedKey(isPeople, field_key);
      const created = await readBackCreatedField(isPeople, field_key);
      if (!created?.id) {
        // The property exists but without its slot key the column cannot be
        // mapped safely, so leave the row alone rather than send it down the
        // built-in path and write the value where nothing reads it.
        closeCreateFieldForm();
        setErrorMessage({
          id: "create_field",
          message: MAPPING_COPY.FIELD_CREATED_UNREADABLE,
        });
        return;
      }

      registerCreatedField(created, isPeople);

      const updated_columns = withFieldMapped(
        allColumns,
        [createFieldRow?.index],
        created,
      );
      setAllColumns(updated_columns);
      settingImportTypeValues(updated_columns);
      clearErrorMessage();
      closeCreateFieldForm();
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
    } catch (error) {
      setCreateFieldError(
        getErrorMessage(error?.data?.data?.errors || {}, error?.data?.message),
      );
    } finally {
      setCreatingField(false);
    }
  };

  // Bulk creation is an orchestration layer over the single path above: same
  // name rules, same create call, same read-back. A real customer file needed
  // 23 new properties for its 34 columns, which is 23 trips through the form.
  //
  // Sorts every selected-but-unmapped column into what can be created, what the
  // rep has to map by hand, and what a property they already own covers.
  const buildBulkFieldPlan = () => {
    const to_create = [];
    const builtin_blocked = [];
    const reusable = [];
    const unnamed = [];
    const by_key = {};
    allColumns?.forEach((column, index) => {
      if (
        !column?.column?.length ||
        !column?.selected ||
        column?.property_value
      ) {
        return;
      }
      // Per row, never per batch: the rep can flip any row to Company and the
      // property has to be created against that object type.
      const is_people = column?.property_type === STRINGS.PEOPLE_SMALL;
      const { clean_name, field_key, builtin_conflict, existing_custom } =
        inspectFieldName(column?.column, is_people);
      if (!field_key) {
        unnamed.push({ column: column?.column, index });
        return;
      }
      if (builtin_conflict) {
        builtin_blocked.push({
          column: column?.column,
          label: builtin_conflict?.label || field_key,
        });
        return;
      }
      if (existing_custom) {
        reusable.push({
          column: column?.column,
          index,
          is_people,
          field: existing_custom,
        });
        return;
      }
      // Two headings can sanitise down to one key ("Job title", "Job-Title").
      // One property, both columns pointed at it: posting the key twice is a
      // duplicate error on the second call for a property that already exists.
      const bucket_key = `${is_people ? "people" : "company"}:${field_key}`;
      if (by_key[bucket_key]) {
        by_key[bucket_key].indexes.push(index);
        return;
      }
      const entry = {
        field_key,
        clean_name,
        is_people,
        column: column?.column,
        // The row's own People/Company wording, so the preview cannot describe
        // a column differently from the dropdown it was set on.
        type_label:
          column?.property_type_label ||
          IMPORT_COLUMN_PROPERTY_TYPES?.[is_people ? 0 : 1]?.label,
        indexes: [index],
      };
      by_key[bucket_key] = entry;
      to_create.push(entry);
    });
    return { to_create, builtin_blocked, reusable, unnamed };
  };

  const openBulkPanel = async () => {
    const plan = buildBulkFieldPlan();
    closeCreateFieldForm();
    setBulkPanelOpen(true);
    setBulkRun(null);
    setBulkError("");
    // Resolved before the rep commits, not on request 12 of 23: with no group
    // for an object type the CRM answers "group name is required" to what
    // looked like a property call, and that is a refusal, not a retry.
    const needs_people = plan.to_create?.some((entry) => entry?.is_people);
    const needs_company = plan.to_create?.some((entry) => !entry?.is_people);
    try {
      setBulkGroupsLoading(true);
      const people_groups = needs_people ? await fetchPropertyGroups(true) : [];
      const company_groups = needs_company
        ? await fetchPropertyGroups(false)
        : [];
      setBulkGroups({
        people: people_groups?.[0]?.id || "",
        company: company_groups?.[0]?.id || "",
      });
      if (needs_people && !people_groups?.length) {
        setBulkError(MAPPING_COPY.FIELD_NO_GROUP(CRM_OBJECT_TYPE_PEOPLE));
      } else if (needs_company && !company_groups?.length) {
        setBulkError(MAPPING_COPY.FIELD_NO_GROUP(CRM_OBJECT_TYPE_COMPANY));
      }
    } catch (error) {
      setBulkGroups({ people: "", company: "" });
      setBulkError(
        getErrorMessage(error?.data?.data?.errors || {}, error?.data?.message),
      );
    } finally {
      setBulkGroupsLoading(false);
    }
  };

  const closeBulkPanel = () => {
    // Properties are being posted right now; closing would hide the only place
    // that reports which of them landed.
    if (bulkRun?.running) {
      return;
    }
    setBulkPanelOpen(false);
    setBulkError("");
    setBulkRun(null);
  };

  // No round trip: the property is already there, the column only has to point
  // at it. The picker drops the option too, exactly as a manual pick does, so
  // the same property cannot then be handed to a second column.
  const applyReusableMappings = (reusable, columns) => {
    let next_columns = columns;
    reusable?.forEach((item) => {
      next_columns = withFieldMapped(next_columns, [item?.index], item?.field);
    });
    const people_ids = reusable
      ?.filter((item) => item?.is_people)
      ?.map((item) => item?.field?.id);
    const company_ids = reusable
      ?.filter((item) => !item?.is_people)
      ?.map((item) => item?.field?.id);
    if (people_ids?.length) {
      setPeopleCustomFields((previous) =>
        previous?.filter((item) => !people_ids.includes(item?.id)),
      );
    }
    if (company_ids?.length) {
      setCompanyCustomFields((previous) =>
        previous?.filter((item) => !company_ids.includes(item?.id)),
      );
    }
    return next_columns;
  };

  const handleBulkCreateFields = async () => {
    const plan = buildBulkFieldPlan();
    const entries = plan?.to_create || [];
    if (!entries.length) {
      setBulkPanelOpen(false);
      return;
    }
    // Refuse the whole batch rather than create part of one. A run that is
    // known in advance to overrun would leave the rep working out for
    // themselves which of the 23 columns got in before the CRM said no.
    if (bulkCapacityError) {
      setBulkError(bulkCapacityError);
      return;
    }
    if (entries.some((entry) => entry?.is_people) && !bulkGroups?.people) {
      setBulkError(MAPPING_COPY.FIELD_NO_GROUP(CRM_OBJECT_TYPE_PEOPLE));
      return;
    }
    if (entries.some((entry) => !entry?.is_people) && !bulkGroups?.company) {
      setBulkError(MAPPING_COPY.FIELD_NO_GROUP(CRM_OBJECT_TYPE_COMPANY));
      return;
    }

    setBulkError("");
    let working_columns = allColumns;
    if (plan?.reusable?.length) {
      working_columns = applyReusableMappings(plan.reusable, working_columns);
      setAllColumns(working_columns);
    }

    // The run stops at the first failure rather than pushing on: a create that
    // fails at 12 of 23 has almost always failed for a reason the next 11 share
    // (the limit, the group, the session), and 11 more errors would bury the
    // one that matters. Everything created before it is kept and stays mapped.
    const stopBulkRun = (entry, position, message) =>
      setBulkRun((previous) => ({
        ...previous,
        running: false,
        failed: {
          column: entry?.column,
          name: entry?.clean_name,
          key: entry?.field_key,
          message,
        },
        not_attempted: entries.length - (position + 1),
      }));

    setBulkRun({
      running: true,
      total: entries.length,
      current: 0,
      current_name: "",
      created: [],
      failed: null,
      not_attempted: 0,
    });

    // SEQUENTIAL, never Promise.all. The CRM allocates a slot by reading the
    // tenant's count document, picking the lowest free number and writing it
    // back, so two creates in flight together can be handed the SAME slot and
    // the second column then overwrites the first.
    for (let position = 0; position < entries.length; position++) {
      const entry = entries[position];
      setBulkRun((previous) => ({
        ...previous,
        current: position + 1,
        current_name: entry?.clean_name,
      }));
      try {
        const bucket = entry?.is_people ? "people" : "company";
        // Skipped only when this modal already has a 200 for the key: after a
        // stopped run the earlier properties are real, and a retry that posted
        // them again would either duplicate them or fail on every one.
        if (!createdFieldKeysRef.current[bucket].includes(entry?.field_key)) {
          try {
            await createDynamicProperty(
              entry?.is_people,
              entry?.clean_name,
              [entry?.is_people ? bulkGroups?.people : bulkGroups?.company],
              // Text Input for the batch. The rep is not being asked to pick a
              // type 23 times, and text is the only one that cannot reject the
              // values already sitting in the column.
              [FIELD_TYPE_LABELS[0].label],
            );
          } catch (error) {
            // "Name already taken" is the one failure that is not a failure.
            // The CRM derives the key from the name and refuses a second
            // property for it, so the property the rep wants already exists:
            // made from the properties screen, left behind by a run that
            // stopped, or created against a catalogue this modal loaded before
            // it existed. Treating it as fatal drops the column entirely, which
            // is the whole complaint - the field is there, the column is not
            // mapped to it, and the values arrive nowhere. Fall through to the
            // read-back and map to the property that is already sitting there.
            if (!isPropertyNameTakenError(error)) throw error;
          }
          rememberCreatedKey(entry?.is_people, entry?.field_key);
        }
        const created = await readBackCreatedField(
          entry?.is_people,
          entry?.field_key,
        );
        if (!created?.id) {
          // The property exists, only its slot is unknown, and a column mapped
          // without the slot writes where nothing reads.
          stopBulkRun(entry, position, MAPPING_COPY.FIELD_CREATED_UNREADABLE);
          settingImportTypeValues(working_columns);
          return;
        }

        registerCreatedField(created, entry?.is_people);
        // Functional, because the grid stays live while the run goes on and a
        // snapshot taken before the first request would undo anything the rep
        // changed meanwhile. working_columns is only for the import-type read
        // at the end, which no edit of theirs can invalidate.
        working_columns = withFieldMapped(
          working_columns,
          entry?.indexes,
          created,
        );
        setAllColumns((previous) =>
          withFieldMapped(previous, entry?.indexes, created),
        );
        setBulkRun((previous) => ({
          ...previous,
          created: [
            ...(previous?.created || []),
            {
              column: entry?.column,
              name: created?.label || entry?.clean_name,
              key: entry?.field_key,
            },
          ],
        }));
      } catch (error) {
        stopBulkRun(
          entry,
          position,
          getErrorMessage(error?.data?.data?.errors || {}, error?.data?.message),
        );
        settingImportTypeValues(working_columns);
        return;
      }
    }

    setBulkRun((previous) => ({ ...previous, running: false }));
    settingImportTypeValues(working_columns);
    clearErrorMessage();
    setBulkPanelOpen(false);
    EventEmitter.emit(
      EMITTER_SUCCESS,
      MAPPING_COPY.BULK_CREATED_TOAST(entries.length),
    );
  };

  // search is always sent, even empty: the endpoint interpolates it straight
  // into a regex, so an absent param becomes ".*undefined.*" and matches no
  // list at all. Newest first is the endpoint's default sort, which is what
  // makes a list created below findable on the first page.
  const fetchContactLists = async (search) => {
    try {
      setListsLoading(true);
      const query = `?page=1&limit=${LIST_PAGE_SIZE}&search=${encodeURIComponent(
        search || "",
      )}`;
      // getList, not getAllList: only this one pins type:'file' server-side, so a
      // FOLDER can never reach the picker. getAllList returns folders alongside
      // lists and omits the type field, leaving no way to tell them apart here —
      // and a folder id accepted as list_id writes membership rows nothing reads.
      // Trade-off: getList is scoped to the caller's own lists, so team-shared
      // ones no longer appear. Reversible by projecting `type` on getAllList.
      // It also returns a bare array and hardcodes 10 per page, ignoring limit.
      const response = await getRequest(
        navigate,
        contactListEndPoints.getList + query,
      );
      const lists = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      setContactLists(lists);
      return lists;
    } catch (error) {
      setContactLists([]);
      setListError(
        getErrorMessage(error?.data?.data?.errors || {}, error?.data?.message),
      );
      return [];
    } finally {
      setListsLoading(false);
    }
  };

  // Picking a list no longer costs a member-count call: with the cap gone there
  // is nothing to size the file against.

  const handleListChange = (data) => {
    const selected_id = data?.id?.[0];
    // Changing the destination invalidates a capacity error raised for the list
    // that was picked before, so deselecting is enough to clear the block.
    clearErrorMessage();
    if (selected_id === CREATE_LIST_OPTION_ID) {
      setShowCreateList(true);
      setNewListName("");
      setListError("");
      return;
    }
    setShowCreateList(false);
    setListError("");
    if (selected_id === NO_LIST_OPTION_ID) {
      setSelectedListId([]);
      setSelectedListValue([]);
      return;
    }
    setSelectedListId([selected_id]);
    setSelectedListValue([data?.label?.[0]]);
  };

  const handleCreateList = async () => {
    // The list service enforces 3 to 50 after decoding; check the same bounds
    // here so a bad name never costs a round trip.
    const list_name = (newListName || "").trim();
    if (list_name.length < 3) {
      setListError(MAPPING_COPY.LIST_NAME_MIN);
      return;
    }
    if (list_name.length > 50) {
      setListError(MAPPING_COPY.LIST_NAME_MAX);
      return;
    }
    try {
      setCreatingList(true);
      setListError("");
      // No record_ids: this only makes the empty list. The membership rows are
      // written by the import worker once each record exists, on the path that
      // charges nothing.
      await postRequest(navigate, contactListEndPoints.createList, {
        list_name: encodeURIComponent(list_name),
        permission: LIST_PERMISSION_PRIVATE,
        type: LIST_TYPE_FILE,
      });
      // The create call answers with a message and no id, so the id has to come
      // from a re-read. The service stores the name with its first letter
      // upper-cased, hence the case-insensitive match. The re-read deliberately
      // sends no search term: a name holding a regex character would break the
      // endpoint's own regex, and newest-first ordering puts the new list first.
      const lists = await fetchContactLists("");
      const created = lists?.find(
        (item) => item?.list_name?.toLowerCase() === list_name.toLowerCase(),
      );
      if (!created?._id) {
        setListError(MAPPING_COPY.LIST_CREATED_UNREADABLE);
        return;
      }
      clearErrorMessage();
      setSelectedListId([created?._id]);
      setSelectedListValue([created?.list_name]);
      setShowCreateList(false);
      setNewListName("");
    } catch (error) {
      setListError(
        getErrorMessage(error?.data?.data?.errors || {}, error?.data?.message),
      );
    } finally {
      setCreatingList(false);
    }
  };

  const handleClose = () => {
    setUploadFailed(false);
    setFile("");
    setOpen(!open);
  };

  const handleCheckBoxClick = (column) => {
    clearErrorMessage();
    setSelectMatchColumnsOnly(false);
    const newColumn = { ...column };
    newColumn.selected = column?.selected ? false : true;
    const all_columns_new = [];
    for (let i in allColumns) {
      if (allColumns?.[i]?.column !== newColumn?.column) {
        all_columns_new.push(allColumns[i]);
      } else {
        all_columns_new.push(newColumn);
      }
    }
    setAllColumns(all_columns_new);

    settingImportTypeValues(all_columns_new);
  };

  const settingImportTypeValues = (all_columns_new) => {
    const selected_proprty_values = all_columns_new
      .filter((item) => item?.selected && item?.property_value)
      .map((item) => item.property_value);
    const field_selection = checkSelectedFields(selected_proprty_values);
    if (
      field_selection.is_company_field_available &&
      field_selection.is_people_field_available
    ) {
      // update import Type here.
      setImportCreateType(
        importCreateType.filter((item) => item.id !== "update_only"),
      );
      if (uploadTypeId[0] === "update_only") {
        setUploadTypeId([IMPORT_CREATE_TYPE[2].id]);
        setUploadTypeValue([IMPORT_CREATE_TYPE[2].label]);
      }
    } else {
      setImportCreateType([...IMPORT_CREATE_TYPE]);
    }
  };

  const handleUnmatchedClick = () => {
    clearErrorMessage();
    setSelectMatchColumnsOnly(!selectMatchColumnsOnly);
    let tempColumns = [...allColumns];
    let all_new_columns = [];
    tempColumns.forEach((element) => {
      let obj = { ...element };
      if (obj?.property_value) {
        obj.selected = true;
        all_new_columns.push(obj);
      } else {
        obj.selected = false;
        all_new_columns.push(obj);
      }
    });
    setAllColumns(all_new_columns);

    settingImportTypeValues(all_new_columns);
  };

  // Single source of truth for "is this mapping submittable". The CTA's disabled
  // state and handleSubmit's guard both read it, so the button can never be
  // greyed out for a reason the modal refuses to name.
  const validateMapping = () => {
    // now manage all validations using checkSelectedFields function.
    const selected_proprty_values = allColumns
      .filter((item) => item.property_value?.length && item?.selected)
      .map((item) => item.property_value);
    const field_selection = checkSelectedFields(selected_proprty_values);
    if (
      field_selection.is_people_field_available &&
      field_selection.is_company_field_available
    ) {
      // if both company and people data is selected, check for company_website and email field.
      if (
        !(
          selected_proprty_values.includes("company_website") &&
          selected_proprty_values.includes("email")
        )
      ) {
        // here if both are not selected, it will come here and return from here.
        return {
          id: selected_proprty_values.includes("company_website")
            ? "company_website"
            : "email",
          message: IMPORT_COMPANY_WEBSITE_EMAIL_MANDATORY,
        };
      }
    } else if (field_selection.is_company_field_available) {
      // if only company data is selected, then check for company_website.
      if (!selected_proprty_values.includes("company_website")) {
        // here if company_website is not selected and company data is selected.
        return {
          id: "company_website",
          message: IMPORT_COMPANY_WEBSITE_MANDATORY,
        };
      }
    } else if (field_selection.is_people_field_available) {
      // if only people data is selected, then check for email.
      if (!selected_proprty_values.includes("email")) {
        // here if company_website is not selected and company data is selected.
        return {
          id: "email",
          message: IMPORT_FILE_EMAIL,
        };
      }
    } else {
      return { id: "email", message: ATLEAS_ONE_FIELD };
    }
    // The destination list adds no rule of its own: a file of any size goes
    // through, so a sound column mapping is the whole of the check.
    return null;
  };

  const handleSubmit = async () => {
    const validation_error = validateMapping();
    if (validation_error) {
      setErrorMessage(validation_error);
      return;
    }
    // make api call here.

    // The key is written when the upload finishes and cleared on submit, so a
    // second submit — or a reload between upload and submit — leaves it empty.
    // Sending it anyway books an import the worker can never read: the job sits
    // in processing for ever and nothing says why. Stop instead.
    const file_path = localStorage.getItem("upload_file");
    if (!file_path) {
      setErrorMessage({
        id: "file_path",
        message: MAPPING_COPY.FILE_LOST_ERROR,
      });
      return;
    }
    const fill_empty_value = JSON.parse(fillEmptyId[0]);
    const upload_type = uploadTypeId[0];

    const people_fields = [];
    const company_fields = [];
    // Tenant-created fields do NOT travel as a field name. They travel as the
    // pre-allocated slot key (people_N / company_N) the CRM reads and filters on,
    // because a slug-keyed value is written to Mongo/ES and then never shown.
    const crm_people_dynamic_fields = [];
    const crm_company_dynamic_fields = [];
    const missing_index_labels = [];

    for (let i in allColumns) {
      const each_column = allColumns?.[i];
      if (!each_column?.property_value || !each_column?.selected) {
        continue;
      }
      // column_index is a RAW CSV position: the worker parses the file with
      // headers disabled and reads each record by that number. The position in
      // allColumns is not it, because blank headers are skipped there, so using
      // `i` writes every column after a blank header from the wrong source
      // column. csv_index is the only correct value and there is no safe
      // fallback - a bad one silently imports shifted data, so stop instead.
      const column_index = Number(each_column?.csv_index);
      if (!Number.isInteger(column_index) || column_index < 0) {
        missing_index_labels.push(
          each_column?.column || each_column?.property_value,
        );
        continue;
      }
      const is_people_column =
        each_column?.property_type === STRINGS.PEOPLE_SMALL;
      const custom_field_option = findCustomFieldOption(
        each_column?.property_type,
        each_column?.property_value,
      );
      // A tenant property created before slot allocation existed has no
      // reference_field, and the CRM reads those back by their slug instead —
      // see the `reference_field ? reference_field : field` fallback it uses in
      // getContactController, getCompanyController and dealsController. So the
      // slug is the correct key for them, and such a column goes down the
      // built-in path below rather than blocking the whole import.
      if (custom_field_option?.reference_field) {
        const dynamic_field_entry = {
          reference_field: custom_field_option?.reference_field,
          // The property's own key, e.g. "why_fit". Sent so the import can write
          // the column under a name that means something instead of a slot
          // number, which is what every reader downstream then has to translate
          // back — the CRM, the campaign prompt, the leads table, exports.
          //
          // Both are sent on purpose. The import service and its workers key off
          // reference_field today, and a value written under any other key is one
          // the CRM can never read back. Dropping reference_field here before the
          // backend reads field_name would silently break every tenant's import,
          // so it goes when the backend is ready, not before.
          field_name: custom_field_option?.id,
          field_label: custom_field_option?.label || custom_field_option?.id,
          field_type: custom_field_option?.field_type,
          column_index,
        };
        if (is_people_column) {
          crm_people_dynamic_fields.push(dynamic_field_entry);
        } else {
          crm_company_dynamic_fields.push(dynamic_field_entry);
        }
      } else if (is_people_column) {
        people_fields.push({
          field_name: each_column?.property_value,
          column_index,
        });
      } else {
        company_fields.push({
          field_name: each_column?.property_value,
          column_index,
        });
      }
    }

    if (missing_index_labels.length) {
      setErrorMessage({
        id: "column_index",
        message: MAPPING_COPY.MISSING_INDEX_ERROR(missing_index_labels),
      });
      return;
    }

    try {
      setColumnsUploading(true);
      const payload = {
        file_path,
        file_name: file?.name,
        fill_empty_value,
        upload_type,
        people_fields,
        company_fields,
        crm_people_dynamic_fields,
        crm_company_dynamic_fields,
        user_email: userDetailsSelector?.email,
        add_to_crm: addToCrm,
        add_to_imported: addToImported,
      };
      // Optional destination list. The key is only attached when the rep chose
      // one, so an import without a list posts exactly the body it posted
      // before this control existed.
      if (selectedListId?.[0]) {
        payload.list_id = selectedListId[0];
      }
      localStorage.removeItem("upload_file");
      const response = await postRequest(
        navigate,
        importEndpoints.updateFieldMapping,
        payload,
      );
      getImportedFilesList();
      handleClose();
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(error?.data?.data?.errors || {}, error?.data?.message),
      );
    } finally {
      setColumnsUploading(false);
    }
  };

  const renderableColumns = allColumns?.filter((item) => item?.column?.length);
  const mappedCount =
    renderableColumns?.filter((item) => item?.selected && item?.property_value)
      ?.length || 0;
  const totalCount = renderableColumns?.length || 0;
  const blockingError = validateMapping();

  // Counted per object type and de-duplicated by id, because a field created
  // here is reported back to the parent and so can appear both in the props and
  // in createdCustomFields, while the same id is legitimately reusable across
  // Contact and Company. Deal properties are not in either list and are not
  // counted, which is why the copy says the limit is shared with them.
  const countUniqueFields = (fields) =>
    new Set((fields || []).map((item) => item?.id).filter(Boolean)).size;
  const customFieldsUsed =
    countUniqueFields(getTenantCustomFields(true)) +
    countUniqueFields(getTenantCustomFields(false));
  const remainingFieldCapacity = Math.max(
    DYNAMIC_FIELD_LIMIT - customFieldsUsed,
    0,
  );

  // Recomputed every render rather than frozen when the panel opens: the grid
  // stays live behind it, and a preview that promised a column the rep has
  // since unticked would create a property nobody asked for.
  const bulkPlan = buildBulkFieldPlan();
  const bulkCreatableCount = bulkPlan?.to_create?.length || 0;
  const bulkUnmappedCount =
    bulkCreatableCount +
    (bulkPlan?.builtin_blocked?.length || 0) +
    (bulkPlan?.reusable?.length || 0) +
    (bulkPlan?.unnamed?.length || 0);
  const bulkRunning = Boolean(bulkRun?.running);
  // The run keeps its outcome after it stops, which is the only report of what
  // a partial run left on the server.
  const bulkFinished = Boolean(bulkRun && !bulkRun?.running);
  // Read by the panel and by the run itself, so the batch can never be refused
  // for a reason the preview did not already show, with the same numbers in it.
  const bulkCapacityError =
    bulkCreatableCount > remainingFieldCapacity
      ? MAPPING_COPY.BULK_OVER_CAPACITY(
          bulkCreatableCount,
          remainingFieldCapacity,
          DYNAMIC_FIELD_LIMIT,
        )
      : "";

  // Deliberately outside filterByPropertySearch: the rep reaches for this row
  // exactly when the search turned up nothing, so it has to survive an empty
  // result.
  const createFieldOption = {
    id: CREATE_FIELD_OPTION_ID,
    label: MAPPING_COPY.CREATE_FIELD_OPTION,
  };

  const listOptions = [
    { id: NO_LIST_OPTION_ID, label: MAPPING_COPY.NO_LIST_OPTION },
    ...(contactLists || [])
      ?.filter((item) => item?._id && item?.list_name)
      ?.map((item) => ({ id: item?._id, label: item?.list_name })),
    { id: CREATE_LIST_OPTION_ID, label: MAPPING_COPY.CREATE_LIST_OPTION },
  ];

  const importModal = {
    heading: {
      id: STRINGS.IMPORT_HEADER_ID,
      label: STRINGS.MAP_THE_COLUMN,
    },
    description: {
      id: "import_mapping_description",
      label: MAPPING_COPY.DESCRIPTION,
    },
    steps: totalCount
      ? {
          id: "import_mapping_progress",
          label: MAPPING_COPY.MAPPED_COUNT(mappedCount, totalCount),
          bgcolor: mappedCount ? SUCCESS.W_500 : WARNING.W_600,
        }
      : undefined,
  };

  const importModalRightFooter = (
    <div className={classes.importModalRightFooter}>
      <Button
        variant={"ghost"}
        size={"sm36"}
        label={STRINGS.CANCEL}
        onClick={handleClose}
      />
      <Button
        variant={"primary"}
        size={"sm36"}
        label={MAPPING_COPY.IMPORT_CTA(mappedCount)}
        onClick={handleSubmit}
        isLoading={columnsUploading}
        // An open field form, or an open bulk preview, means at least one
        // column's target is still undecided. Importing through it would
        // silently drop that column, and importing through a run in flight
        // would drop whichever ones had not been created yet.
        disabled={
          Boolean(blockingError) ||
          columnsUploading ||
          Boolean(createFieldRow) ||
          bulkPanelOpen
        }
      />
    </div>
  );

  // A greyed-out button with no explanation is the thing reps get stuck on, so the
  // blocking reason sits next to it rather than waiting for a click.
  const importModalLeftFooter = (
    <div style={styles.footerHint}>
      {createFieldRow || bulkPanelOpen || blockingError ? (
        <>
          <IconComponent
            iconLabel={ICON_LABELS.INFO}
            color={WARNING.W_600}
            fontSize={"16px"}
          />
          <TypographyInline
            size={"xs"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={
              createFieldRow
                ? MAPPING_COPY.FINISH_FIELD_FIRST
                : bulkPanelOpen
                  ? MAPPING_COPY.BULK_FINISH_FIRST
                  : blockingError?.message
            }
          />
        </>
      ) : null}
    </div>
  );

  const csvMatchTableHeadersLabel = [
    {
      id: STRINGS.MATCHED,
      label: STRINGS.MATCHED,
    },
    {
      id: STRINGS.COLUMN_HEADER,
      label: STRINGS.COLUMN_HEADER,
    },
    {
      id: STRINGS.PROPERTY_TYPE,
      label: STRINGS.PROPERTY_TYPE,
    },
    {
      id: STRINGS.REACHIQ_PROPERTY,
      label: STRINGS.REACHIQ_PROPERTY,
    },
    {
      id: MAPPING_COPY.STATUS,
      label: MAPPING_COPY.STATUS,
    },
  ];

  // One shape for every list in the bulk panel: the column on the left, what is
  // going to happen to it on the right.
  const renderBulkList = (rows) => (
    <div style={styles.bulkList}>
      {rows?.map((row) => (
        <div style={styles.bulkListRow} key={row?.id}>
          <TypographyInline
            size={"xs"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={row?.left}
            isEllipses
          />
          <TypographyInline
            size={"xs"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.MEDIUM}
            label={row?.right}
            isEllipses
          />
        </div>
      ))}
    </div>
  );

  return (
    <Box>
      {/* Upload Progress bar modal */}
      <Modal open={uploadPercentage !== 100 && open && !uploadFailed}>
        <ProgressEmitter
          name={file?.name}
          uploadPercentage={uploadPercentage}
          refreshRequired={true}
        />
      </Modal>
      {/* Upload Progress bar modal */}

      {/* CSV Mapping Modal */}
      <CustomModal
        open={uploadPercentage === 100 && open && !uploadFailed}
        close={handleClose}
        header={importModal}
        width={"70.521vw"}
        footer={{ left: importModalLeftFooter, right: importModalRightFooter }}
      >
        <div
          className={classes.importModalColumnsContainer}
          style={styles.modalBodyScroll}
        >
          {totalCount ? (
            <Box
              sx={{
                ...styles.gridScrollContainer,
                // Give the inline field form, or the bulk preview, its room back
                // rather than letting the two together outgrow the modal.
                maxHeight: createFieldRow || bulkPanelOpen ? "24vh" : "40vh",
              }}
            >
              <Box sx={styles.gridHeaderRow}>
                {csvMatchTableHeadersLabel?.map((header) => {
                  return (
                    <div
                      key={header?.id}
                      style={
                        header?.id === MAPPING_COPY.STATUS
                          ? { ...styles.gridHeaderCell, textAlign: "right" }
                          : styles.gridHeaderCell
                      }
                    >
                      <TypographyInline
                        size={"sm"}
                        color={GREY.PRIMARY}
                        fontWeight={fontWeight.MEDIUM}
                        label={header?.label}
                      />
                    </div>
                  );
                })}
              </Box>
              {allColumns?.map((eachColumn, i) => {
                // One derived status per row. The chip, the row tint and the
                // dimming all read from it so they cannot contradict each other.
                const statusKey = getRowStatusKey(eachColumn);
                const status = ROW_STATUS[statusKey];
                const isPeopleColumn =
                  eachColumn?.property_type === STRINGS.PEOPLE_SMALL;
                const targetOptions = isPeopleColumn
                  ? peopleCustomFields
                  : companyCustomFields;
                return eachColumn?.column?.length ? (
                  <Box
                    key={`${eachColumn?.column}_${i}`}
                    sx={{
                      ...styles.gridRow,
                      backgroundColor: status?.background,
                      opacity: statusKey === "skipped" ? 0.65 : 1,
                    }}
                  >
                    <div style={styles.checkboxCell}>
                      <Checkbox
                        checked={
                          // selectMatchColumnsOnly
                          //   ? eachColumn.property_value
                          //   :
                          eachColumn?.selected
                        }
                        onChange={() => handleCheckBoxClick(eachColumn)}
                      />
                    </div>
                    <div style={styles.columnNameCell}>
                      <TypographyInline
                        size={"sm"}
                        color={GREY.PRIMARY}
                        fontWeight={fontWeight.MEDIUM}
                        label={eachColumn?.column}
                        isEllipses
                      />
                      {/* The first row's value is the cheapest way to spot an
                          off-by-one or a file split on the wrong delimiter. */}
                      <TypographyInline
                        size={"xs"}
                        color={GREY.TERTIARY}
                        fontWeight={fontWeight.REGULAR}
                        label={
                          eachColumn?.sample_value
                            ? `${MAPPING_COPY.SAMPLE_PREFIX}${eachColumn?.sample_value}`
                            : MAPPING_COPY.NO_SAMPLE
                        }
                        isEllipses
                      />
                    </div>
                    {/* Property dropdown */}
                    <Box sx={styles.gridCell}>
                      <AutoCompleteCustom
                        items={IMPORT_COLUMN_PROPERTY_TYPES}
                        size={"sm36"}
                        idState={[eachColumn?.property_type]}
                        valueState={[eachColumn?.property_type_label]}
                        dropdownId={`import_property_type_${i}`}
                        onChange={(data) =>
                          handlePropertyTypeChange(data, i, eachColumn)
                        }
                      />
                    </Box>
                    {/* Columns Dropdown */}
                    <Box sx={styles.gridCell}>
                      <AutoCompleteCustom
                        items={filterByPropertySearch(
                          targetOptions?.filter((item) => !item?.isCustomField),
                          i,
                        )}
                        subItems={[
                          ...(filterByPropertySearch(
                            targetOptions?.filter((item) => item?.isCustomField),
                            i,
                          ) || []),
                          createFieldOption,
                        ]}
                        subItemsLabel={STRINGS.CUSTOM_FIELDS}
                        size={"sm36"}
                        // Was property_type, which never matches an option id, so
                        // the selected row was never highlighted. The setters stay
                        // no-ops on purpose: allColumns is the source of truth and
                        // onChange is what writes it.
                        idState={[eachColumn?.property_value]}
                        setIdState={() => {}}
                        valueState={[eachColumn?.property_label]}
                        setValueState={() => {}}
                        // Without a unique id every row shared the DOM id
                        // "custom-dropdown" and the menu measured its width off
                        // whichever row getElementById happened to hit first.
                        dropdownId={`import_reachiq_property_${i}`}
                        isSearchEnable
                        handleSearch={getPropertySearchHandler(i)}
                        handleClick={() =>
                          setPropertySearch((prev) => ({ ...prev, [i]: "" }))
                        }
                        onChange={(data) =>
                          handlePropertyValueChange(
                            data,
                            i,
                            eachColumn?.property_value,
                            eachColumn?.property_type,
                          )
                        }
                      />
                    </Box>
                    <div style={styles.statusCell}>
                      <div style={styles.statusStack}>
                        {status?.variant ? (
                          <StatusChip
                            variant={status?.variant}
                            label={status?.label}
                          />
                        ) : (
                          <TypographyInline
                            size={"xs"}
                            color={GREY.TERTIARY}
                            fontWeight={fontWeight.MEDIUM}
                            label={status?.label}
                            isNoWrap
                          />
                        )}
                      </div>
                    </div>
                  </Box>
                ) : null;
              })}
            </Box>
          ) : columnsParsed ? (
            <Box sx={styles.emptyState}>
              <IconComponent
                iconLabel={ICON_LABELS.DESCRIPTION}
                color={GREY.TERTIARY}
                fontSize={"24px"}
              />
              <TypographyInline
                size={"sm"}
                color={GREY.PRIMARY}
                fontWeight={fontWeight.MEDIUM}
                label={MAPPING_COPY.EMPTY_TITLE}
              />
              <TypographyInline
                size={"xs"}
                color={GREY.SECONDARY}
                fontWeight={fontWeight.REGULAR}
                label={MAPPING_COPY.EMPTY_DETAIL}
              />
            </Box>
          ) : null}

          {/* One action for the whole file. The per-row option below stays
              exactly as it was; this is for the file that needs 23 of them. */}
          {bulkCreatableCount > 0 && !createFieldRow && !bulkPanelOpen ? (
            <Box sx={styles.bulkBar}>
              <div style={styles.bulkBarText}>
                <IconComponent
                  iconLabel={ICON_LABELS.ADD_CIRCLE_OUTLINE}
                  color={PURPLE.W_500}
                  fontSize={"16px"}
                />
                <TypographyInline
                  size={"xs"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label={MAPPING_COPY.BULK_BAR_TEXT(bulkUnmappedCount)}
                  isEllipses
                />
              </div>
              <Button
                variant={"ghost"}
                size={"sm36"}
                label={MAPPING_COPY.BULK_CTA(bulkCreatableCount)}
                onClick={openBulkPanel}
              />
            </Box>
          ) : null}

          {/* Nothing is posted until the rep has seen the count, the capacity,
              the keys the CRM will derive, and what is being left out. */}
          {bulkPanelOpen ? (
            <Box sx={styles.createFieldPanel}>
              <div style={styles.createFieldHeader}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.MEDIUM}
                  label={MAPPING_COPY.BULK_TITLE}
                />
                <TypographyInline
                  size={"xs"}
                  color={remainingFieldCapacity ? GREY.TERTIARY : ERROR.W_500}
                  fontWeight={fontWeight.REGULAR}
                  label={MAPPING_COPY.FIELD_CAPACITY(
                    customFieldsUsed,
                    DYNAMIC_FIELD_LIMIT,
                  )}
                  isNoWrap
                />
              </div>

              {bulkRunning ? (
                <TypographyInline
                  size={"sm"}
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.MEDIUM}
                  label={MAPPING_COPY.BULK_PROGRESS(
                    bulkRun?.current,
                    bulkRun?.total,
                    bulkRun?.current_name,
                  )}
                />
              ) : bulkFinished && bulkRun?.failed ? (
                <>
                  <TypographyInline
                    size={"sm"}
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.MEDIUM}
                    label={MAPPING_COPY.BULK_FAILED_TITLE(
                      bulkRun?.created?.length || 0,
                      bulkRun?.total,
                    )}
                  />
                  {bulkRun?.created?.length ? (
                    <div style={styles.bulkSection}>
                      <TypographyInline
                        size={"xs"}
                        color={GREY.SECONDARY}
                        fontWeight={fontWeight.REGULAR}
                        label={MAPPING_COPY.BULK_CREATED_LIST}
                      />
                      {renderBulkList(
                        bulkRun?.created?.map((item, position) => ({
                          id: `${item?.key}_${position}`,
                          left: item?.column,
                          right: item?.key,
                        })),
                      )}
                    </div>
                  ) : null}
                  <Box sx={{ ...styles.errorBanner, marginTop: "0px" }}>
                    <IconComponent
                      iconLabel={ICON_LABELS.INFO}
                      color={ERROR.W_500}
                      fontSize={"16px"}
                    />
                    <ErrorMessage
                      message={`${MAPPING_COPY.BULK_FAILED_DETAIL(
                        bulkRun?.failed?.name,
                      )} ${bulkRun?.failed?.message}`}
                      marginBottom={"0px"}
                      padding={"0px"}
                    />
                  </Box>
                  {bulkRun?.not_attempted ? (
                    <TypographyInline
                      size={"xs"}
                      color={GREY.SECONDARY}
                      fontWeight={fontWeight.REGULAR}
                      label={MAPPING_COPY.BULK_NOT_ATTEMPTED(
                        bulkRun?.not_attempted,
                      )}
                    />
                  ) : null}
                </>
              ) : (
                <>
                  <TypographyInline
                    size={"sm"}
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.REGULAR}
                    label={MAPPING_COPY.BULK_SUMMARY(
                      bulkCreatableCount,
                      remainingFieldCapacity,
                    )}
                  />
                  {bulkCreatableCount ? (
                    <div style={styles.bulkSection}>
                      <TypographyInline
                        size={"xs"}
                        color={GREY.SECONDARY}
                        fontWeight={fontWeight.REGULAR}
                        label={MAPPING_COPY.BULK_KEYS_NOTE}
                      />
                      {renderBulkList(
                        bulkPlan?.to_create?.map((entry) => ({
                          id: `${entry?.is_people ? "people" : "company"}_${
                            entry?.field_key
                          }`,
                          left: entry?.column,
                          right: `${entry?.field_key} · ${entry?.type_label}`,
                        })),
                      )}
                    </div>
                  ) : null}
                  {bulkPlan?.builtin_blocked?.length ? (
                    <div style={styles.bulkSection}>
                      <TypographyInline
                        size={"xs"}
                        color={WARNING.W_700}
                        fontWeight={fontWeight.REGULAR}
                        label={MAPPING_COPY.BULK_BUILTIN_EXCLUDED(
                          bulkPlan?.builtin_blocked?.length,
                        )}
                      />
                      {renderBulkList(
                        bulkPlan?.builtin_blocked?.map((item, position) => ({
                          id: `${item?.column}_${position}`,
                          left: item?.column,
                          right: item?.label,
                        })),
                      )}
                    </div>
                  ) : null}
                  {bulkPlan?.reusable?.length ? (
                    <div style={styles.bulkSection}>
                      <TypographyInline
                        size={"xs"}
                        color={GREY.SECONDARY}
                        fontWeight={fontWeight.REGULAR}
                        label={MAPPING_COPY.BULK_REUSE(
                          bulkPlan?.reusable?.length,
                        )}
                      />
                      {renderBulkList(
                        bulkPlan?.reusable?.map((item) => ({
                          id: `reuse_${item?.index}`,
                          left: item?.column,
                          right: item?.field?.label || item?.field?.id,
                        })),
                      )}
                    </div>
                  ) : null}
                  {bulkPlan?.unnamed?.length ? (
                    <div style={styles.bulkSection}>
                      <TypographyInline
                        size={"xs"}
                        color={GREY.SECONDARY}
                        fontWeight={fontWeight.REGULAR}
                        label={MAPPING_COPY.BULK_UNNAMED(
                          bulkPlan?.unnamed?.length,
                        )}
                      />
                      {renderBulkList(
                        bulkPlan?.unnamed?.map((item) => ({
                          id: `unnamed_${item?.index}`,
                          left: item?.column,
                          right: MAPPING_COPY.FIELD_NAME_REQUIRED,
                        })),
                      )}
                    </div>
                  ) : null}
                  <TypographyInline
                    size={"xs"}
                    color={GREY.TERTIARY}
                    fontWeight={fontWeight.REGULAR}
                    label={MAPPING_COPY.BULK_TYPE_NOTE}
                  />
                  <TypographyInline
                    size={"xs"}
                    color={GREY.TERTIARY}
                    fontWeight={fontWeight.REGULAR}
                    label={MAPPING_COPY.BULK_STOP_NOTE}
                  />
                </>
              )}

              {/* Capacity is checked here rather than only on click, so the rep
                  sees the refusal with its real numbers before committing. */}
              {!bulkRunning && (bulkCapacityError || bulkError) ? (
                <Box sx={{ ...styles.errorBanner, marginTop: "0px" }}>
                  <IconComponent
                    iconLabel={ICON_LABELS.INFO}
                    color={ERROR.W_500}
                    fontSize={"16px"}
                  />
                  <ErrorMessage
                    message={bulkCapacityError || bulkError}
                    marginBottom={"0px"}
                    padding={"0px"}
                  />
                </Box>
              ) : null}

              <div style={styles.createFieldActions}>
                <Button
                  variant={"ghost"}
                  size={"sm36"}
                  label={
                    bulkRun?.failed
                      ? MAPPING_COPY.BULK_CLOSE_CTA
                      : STRINGS.CANCEL
                  }
                  onClick={closeBulkPanel}
                  disabled={bulkRunning}
                />
                <Button
                  variant={"primary"}
                  size={"sm36"}
                  label={
                    bulkRun?.failed
                      ? MAPPING_COPY.BULK_RETRY_CTA(bulkCreatableCount)
                      : MAPPING_COPY.BULK_CONFIRM_CTA(bulkCreatableCount)
                  }
                  onClick={handleBulkCreateFields}
                  isLoading={bulkRunning || bulkGroupsLoading}
                  disabled={
                    bulkRunning ||
                    bulkGroupsLoading ||
                    !bulkCreatableCount ||
                    Boolean(bulkCapacityError) ||
                    Boolean(bulkError)
                  }
                />
              </div>
            </Box>
          ) : null}

          {/* Inline field creation, so a missing property does not send the rep
              to the properties screen and back through the upload again. */}
          {createFieldRow ? (
            <Box sx={styles.createFieldPanel}>
              <div style={styles.createFieldHeader}>
                <TypographyInline
                  size={"sm"}
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.MEDIUM}
                  label={MAPPING_COPY.CREATE_FIELD_TITLE(
                    createFieldRow?.column,
                  )}
                  isEllipses
                />
              </div>
              <div style={styles.createFieldGrid}>
                <div style={styles.createFieldControl}>
                  <TypographyInline
                    size={"sm"}
                    color={GREY.QUATINARY}
                    fontWeight={fontWeight.MEDIUM}
                    label={MAPPING_COPY.FIELD_NAME_LABEL}
                    isRequired
                  />
                  <InputField
                    size={"sm36"}
                    value={createFieldName}
                    placeholder={MAPPING_COPY.FIELD_NAME_PLACEHOLDER}
                    onChange={(e) => {
                      setCreateFieldError("");
                      setCreateFieldName(e?.target?.value);
                    }}
                  />
                </div>
              </div>
              {createFieldError ? (
                // The banner carries its own top margin for the grid below the
                // table; inside this flex column the gap already provides it.
                <Box sx={{ ...styles.errorBanner, marginTop: "0px" }}>
                  <IconComponent
                    iconLabel={ICON_LABELS.INFO}
                    color={ERROR.W_500}
                    fontSize={"16px"}
                  />
                  <ErrorMessage
                    message={createFieldError}
                    marginBottom={"0px"}
                    padding={"0px"}
                  />
                </Box>
              ) : null}
              <div style={styles.createFieldActions}>
                <Button
                  variant={"ghost"}
                  size={"sm36"}
                  label={STRINGS.CANCEL}
                  onClick={closeCreateFieldForm}
                />
                <Button
                  variant={"primary"}
                  size={"sm36"}
                  label={MAPPING_COPY.CREATE_FIELD_CTA}
                  onClick={handleCreateField}
                  isLoading={creatingField}
                  disabled={creatingField}
                />
              </div>
            </Box>
          ) : null}

          {errorMessage?.id ? (
            <Box sx={styles.errorBanner}>
              <IconComponent
                iconLabel={ICON_LABELS.INFO}
                color={ERROR.W_500}
                fontSize={"16px"}
              />
              <ErrorMessage
                message={errorMessage.message}
                marginBottom={"0px"}
                padding={"0px"}
              />
            </Box>
          ) : null}
          <Box sx={styles.flex}>
            <Checkbox
              checked={selectMatchColumnsOnly}
              onClick={handleUnmatchedClick}
              label={STRINGS.UNMATCHED_COLUMN_WARNING}
            />
            <Checkbox
              checked={addToCrm}
              onClick={() => setAddToCrm(!addToCrm)}
              label={STRINGS.ADD_TO_CRM}
            />
            <Checkbox
              checked={addToImported}
              onClick={() => setAddToImported(!addToImported)}
              label={STRINGS.ADD_TO_IMPORTED}
            />
          </Box>

          <Box sx={styles.containDropdowns}>
            <Box>
              <TypographyInline
                size={"sm"}
                color={GREY.SECONDARY}
                fontWeight={fontWeight.REGULAR}
                label={STRINGS.SELECT_IMPORT_TYPE}
              />
              <Box sx={styles.dropdown}>
                <AutoCompleteCustom
                  size={"sm36"}
                  items={importCreateType}
                  idState={uploadTypeId}
                  setIdState={setUploadTypeId}
                  valueState={uploadTypeValue}
                  setValueState={setUploadTypeValue}
                  paperHeight={"fit-content"}
                  dropdownId="import_dropdown"
                />
              </Box>
            </Box>
            <Box>
              <TypographyInline
                size={"sm"}
                color={GREY.SECONDARY}
                fontWeight={fontWeight.REGULAR}
                label={STRINGS.EMPTY_VALUES}
              />
              <Box sx={styles.dropdown}>
                <AutoCompleteCustom
                  size={"sm36"}
                  items={IMPORT_EMPTY_FIELD_OPTIONS}
                  idState={fillEmptyId}
                  setIdState={setFillEmptyId}
                  valueState={fillEmptyValue}
                  setValueState={setFillEmptyValue}
                  paperHeight={"fit-content"}
                  dropdownId="import_dropdown_empty"
                />
              </Box>
            </Box>
            <Box>
              <TypographyInline
                size={"sm"}
                color={GREY.SECONDARY}
                fontWeight={fontWeight.REGULAR}
                label={MAPPING_COPY.LIST_LABEL}
              />
              <Box sx={styles.dropdown}>
                <AutoCompleteCustom
                  size={"sm36"}
                  items={listOptions}
                  isLoading={listsLoading}
                  idState={selectedListId}
                  setIdState={() => {}}
                  valueState={selectedListValue}
                  setValueState={() => {}}
                  onChange={handleListChange}
                  placeholder={MAPPING_COPY.LIST_PLACEHOLDER}
                  isSearchEnable
                  handleSearch={handleListSearch}
                  // The menu's own search box mounts empty every time, so a stale
                  // term would silently keep filtering what it shows.
                  handleClick={() => setListSearch("")}
                  dropdownId="import_contact_list"
                />
              </Box>
            </Box>
          </Box>

          <Box sx={styles.listSection}>
            {/* Said out loud because "add to a list" reads like a move, and reps
                have been burned by imports that vanished from Imported. */}
            <TypographyInline
              size={"xs"}
              color={GREY.TERTIARY}
              fontWeight={fontWeight.REGULAR}
              label={MAPPING_COPY.LIST_HINT}
            />
            <div style={styles.listControls}>
              {showCreateList ? (
                <div style={styles.listCreateRow}>
                  <div style={styles.listCreateInput}>
                    <InputField
                      size={"sm36"}
                      value={newListName}
                      placeholder={MAPPING_COPY.LIST_NAME_PLACEHOLDER}
                      onChange={(e) => {
                        setListError("");
                        setNewListName(e?.target?.value);
                      }}
                      variant={listError ? "error" : "default"}
                      errormessage={listError}
                    />
                  </div>
                  <Button
                    variant={"primary"}
                    size={"sm36"}
                    label={STRINGS.CREATE}
                    onClick={handleCreateList}
                    isLoading={creatingList}
                    disabled={creatingList}
                  />
                  <Button
                    variant={"ghost"}
                    size={"sm36"}
                    label={STRINGS.CANCEL}
                    onClick={() => {
                      setShowCreateList(false);
                      setNewListName("");
                      setListError("");
                    }}
                  />
                </div>
              ) : null}
            </div>
            {!showCreateList && listError ? (
              <TypographyInline
                size={"xs"}
                color={ERROR.W_500}
                fontWeight={fontWeight.REGULAR}
                label={listError}
              />
            ) : null}
          </Box>
        </div>
      </CustomModal>
    </Box>
  );
};

export default MappingFieldModal;
